import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Briefcase, Building2, ArrowRight, ChevronDown } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import api from "@/lib/api";

function JobCard({ job, open, onToggle }) {
  return (
    <div className="border-b border-slate-200 transition-colors duration-500 hover:bg-brand-light/20" data-testid={`job-card-${job.id}`}>
      <button onClick={onToggle} className="w-full text-left py-8 px-4 -mx-4 grid lg:grid-cols-12 gap-4 lg:gap-8 items-start group"
        data-testid={`job-toggle-${job.id}`}>
        <div className="lg:col-span-5">
          <h3 className="font-display font-semibold text-slate-900 text-lg sm:text-xl group-hover:text-brand-red transition-colors">{job.title}</h3>
          {job.client && (
            <p className="mt-1.5 flex items-center gap-2 text-sm text-slate-500">
              <Building2 size={14} className="text-brand-red" /> {job.client}
            </p>
          )}
        </div>
        <div className="lg:col-span-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
          {job.location && <span className="flex items-center gap-2"><MapPin size={14} className="text-brand-red" />{job.location}</span>}
          {job.employment_type && <span className="flex items-center gap-2"><Clock size={14} className="text-brand-red" />{job.employment_type}</span>}
          {job.experience && <span className="flex items-center gap-2"><Briefcase size={14} className="text-brand-red" />{job.experience}</span>}
        </div>
        <div className="lg:col-span-2 flex lg:justify-end">
          <span className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${open ? "text-brand-red" : "text-slate-400 group-hover:text-brand-red"}`}>
            {open ? "Close" : "View Role"}
            <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </span>
        </div>
      </button>

      {open && (
        <div className="pb-10 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-6">
            {job.description && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2.5">Job Description</p>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed whitespace-pre-line">{job.description}</p>
              </div>
            )}
            {job.responsibilities?.length > 0 && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2.5">Key Responsibilities</p>
                <ul className="space-y-2.5">
                  {job.responsibilities.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-slate-600">
                      <span className="mt-2 h-1.5 w-1.5 rotate-45 bg-brand-red shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="lg:col-span-5 space-y-6">
            {job.skills?.length > 0 && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2.5">Required Skills & Qualifications</p>
                <div className="flex flex-wrap gap-2">
                  {job.skills.map((s) => (
                    <span key={s} className="border border-slate-200 px-3.5 py-1.5 text-xs text-slate-600">{s}</span>
                  ))}
                </div>
              </div>
            )}
            <Link to={`/careers/apply?role=${encodeURIComponent(job.title)}`} data-testid={`job-apply-${job.id}`}
              className="group/btn inline-flex items-center gap-2 bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-7 py-3.5 transition-all duration-300 hover:-translate-y-0.5">
              Upload CV
              <ArrowRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CurrentOpenings() {
  const [jobs, setJobs] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get("/jobs").then((res) => {
      setJobs(res.data);
      if (res.data.length > 0) setOpenId(res.data[0].id);
    }).catch(() => setJobs([]));
  }, []);

  return (
    <>
      <section className="pt-[124px] pb-10 lg:pt-[150px] lg:pb-14 relative overflow-hidden" data-testid="openings-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full grid grid-cols-2 lg:grid-cols-4">
            <div className="border-l border-slate-200/60" />
            <div className="border-l border-slate-200/60" />
            <div className="border-l border-slate-200/60 hidden lg:block" />
            <div className="border-l border-r border-slate-200/60 hidden lg:block" />
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MaskLine delay={0.05}>
            <span className="flex items-center gap-4">
              <span className="h-px w-12 bg-brand-red" />
              <span className="font-mono text-[11px] uppercase tracking-[0.38em] text-brand-red font-medium">Find Jobs</span>
            </span>
          </MaskLine>
          <h1 className="mt-7 font-display font-extrabold tracking-tight text-slate-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.04]">
            <MaskLine delay={0.18}>Browse <span className="text-brand-red">Jobs.</span></MaskLine>
          </h1>
          <MaskLine delay={0.32} className="mt-7 max-w-2xl">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Explore opportunities with our client organizations. Each role is handled with a focused, confidential recruitment process — find the one that fits.
            </p>
          </MaskLine>
        </div>
      </section>

      <section className="pb-14 lg:pb-20" data-testid="openings-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {jobs === null ? (
            <p className="py-16 text-sm text-slate-400">Loading openings…</p>
          ) : jobs.length === 0 ? (
            <Reveal>
              <div className="border-t border-b border-slate-200 py-16 text-center" data-testid="openings-empty">
                <p className="font-display font-semibold text-slate-900 text-xl">No openings published right now.</p>
                <p className="mt-3 text-sm sm:text-base text-slate-500 max-w-md mx-auto">
                  New roles are added regularly. Share your resume with us and we'll reach out when a matching opportunity opens.
                </p>
                <Link to="/careers/apply" data-testid="openings-empty-apply"
                  className="mt-7 inline-flex items-center gap-2 bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5">
                  Upload CV <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          ) : (
            <div className="border-t border-slate-200">
              {jobs.map((job, i) => (
                <Reveal key={job.id} delay={i * 0.04}>
                  <JobCard job={job} open={openId === job.id} onToggle={() => setOpenId(openId === job.id ? null : job.id)} />
                </Reveal>
              ))}
              <Reveal delay={0.1}>
                <p className="pt-10 text-sm text-slate-500">
                  Don't see the right role?{" "}
                  <Link to="/careers/apply" className="text-brand-red font-medium hover:text-brand-dark transition-colors" data-testid="openings-general-apply">
                    Share your resume anyway
                  </Link>{" "}
                  — we connect with professionals beyond advertised positions.
                </p>
              </Reveal>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
